import PropTypes from "prop-types";
// @mui
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
// components
import Iconify from "src/components/iconify";
//
import {
  conditionToText,
  filterToText,
  isCondition,
  isEmptyFilter,
  joinWith,
  splitAnd,
} from "../core/filter";

export default function FilterPanel({ ctx, summary }) {
  const { filterValue, filterEnabled } = ctx;
  const empty = isEmptyFilter(filterValue);
  const items = empty ? [] : splitAnd(filterValue);
  const simple = items !== null && items.every(item => isCondition(item));

  const removeAt = index => {
    const next = items.filter((_, position) => position !== index);

    ctx.setFilterValue(joinWith(next));
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      className="cg-filter-panel"
      sx={{
        px: 1.5,
        py: 0.5,
        minHeight: 44,
        borderTop: theme => `1px solid ${theme.palette.divider}`,
      }}
    >
      {empty ? (
        <Button
          size="small"
          color="inherit"
          startIcon={
            <Iconify
              icon="mdi:filter-plus-outline"
              width={18}
            />
          }
          onClick={ctx.openFilterBuilder}
          sx={{ color: "text.secondary" }}
        >
          {ctx.t("gridCreateFilter")}
        </Button>
      ) : (
        <>
          <Tooltip title={ctx.t("gridEnableFilter")}>
            <Checkbox
              size="small"
              checked={filterEnabled}
              onChange={event => ctx.setFilterEnabled(event.target.checked)}
              inputProps={{ "aria-label": ctx.t("gridEnableFilter") }}
              sx={{ p: 0.5 }}
            />
          </Tooltip>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{
              flex: "1 1 auto",
              minWidth: 0,
              overflowX: "auto",
              opacity: filterEnabled ? 1 : 0.5,
              py: 0.25,
            }}
          >
            {simple ? (
              items.map((item, index) => {
                const { caption, operation, value } = conditionToText(
                  item,
                  ctx.columnsBySelector,
                  ctx.locale,
                  ctx.t
                );

                return (
                  <Chip
                    key={index}
                    size="small"
                    variant="soft"
                    label={
                      <span>
                        <strong>{caption}</strong> {operation.toLowerCase()} {value}
                      </span>
                    }
                    onClick={ctx.openFilterBuilder}
                    onDelete={() => removeAt(index)}
                    sx={{ maxWidth: 360 }}
                  />
                );
              })
            ) : (
              <Typography
                variant="body2"
                noWrap
                onClick={ctx.openFilterBuilder}
                sx={{ cursor: "pointer", minWidth: 0 }}
                title={filterToText(filterValue, ctx.columnsBySelector, ctx.locale, ctx.t)}
              >
                {filterToText(filterValue, ctx.columnsBySelector, ctx.locale, ctx.t)}
              </Typography>
            )}
          </Stack>
          <Button
            size="small"
            color="inherit"
            onClick={ctx.clearFilter}
            sx={{ flexShrink: 0, color: "text.secondary" }}
          >
            {ctx.t("gridClearFilter")}
          </Button>
        </>
      )}
      {empty ? <Stack sx={{ flex: "1 1 auto" }} /> : null}
      {summary ? (
        <Typography
          variant="caption"
          noWrap
          sx={{ color: "text.secondary", flexShrink: 0 }}
        >
          {summary}
        </Typography>
      ) : null}
    </Stack>
  );
}

FilterPanel.propTypes = {
  ctx: PropTypes.object,
  summary: PropTypes.string,
};
